import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { api } from '../lib/api';

const PlaidLink = ({ onSuccess, onExit }) => {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch Link token from backend
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.createPlaidLinkToken('default_user');
        setLinkToken(data.link_token);
      } catch (err) {
        console.error('Error fetching Link token:', err);
        setError(err.message || 'Failed to initialize Plaid Link');
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, []);

  const onPlaidSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Plaid Link success - publicToken:', publicToken?.substring(0, 20) + '...', 'metadata:', metadata);
        
        const data = await api.exchangePlaidPublicToken(publicToken);
        console.log('Institution connected successfully:', data);
        
        if (onSuccess) {
          onSuccess(data.institution);
        }
      } catch (err) {
        console.error('Error exchanging public token:', err);
        setError(err.message || 'Failed to connect institution');
        // Don't clear loading state on error so user can see the error
      } finally {
        setLoading(false);
      }
    },
    [onSuccess]
  );

  const onPlaidExit = useCallback(
    (err, metadata) => {
      console.log('Plaid Link exit - err:', err, 'metadata:', metadata);
      
      if (err) {
        console.error('Plaid Link error:', err);
        // Handle specific error types
        if (err.error_code === 'ITEM_LOGIN_REQUIRED') {
          setError('Bank login required. Please try connecting again.');
        } else if (err.error_code === 'INVALID_CREDENTIALS') {
          setError('Invalid bank credentials. Please check your username and password.');
        } else {
          setError(err.error_message || err.error_code || 'Connection cancelled');
        }
      } else {
        // User exited without error (e.g., clicked back/cancel)
        setError(null);
      }
      
      if (onExit) {
        onExit(err, metadata);
      }
    },
    [onExit]
  );

  const config = {
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  };

  const { open, ready } = usePlaidLink(config);

  const handleOpen = () => {
    if (ready && linkToken) {
      open();
    }
  };

  if (loading && !linkToken) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin inline-block mr-2">⏳</div>
        <span>Initializing Plaid Link...</span>
      </div>
    );
  }

  if (error && !linkToken) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800 text-sm">
          <strong>Error:</strong> {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-red-600 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={handleOpen}
        disabled={!ready || loading}
        className={`w-full py-2 px-4 rounded-md text-white font-medium ${
          !ready || loading
            ? 'bg-gray-300 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {loading ? (
          <>
            <span className="animate-spin inline-block mr-2">⏳</span>
            Connecting...
          </>
        ) : (
          'Connect Bank Account'
        )}
      </button>
      
      {error && linkToken && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-xs">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PlaidLink;

